import * as THREE from 'three';
import type { Track, TrackNode } from '@/world/Track';
import type { TrackTheme } from '@/world/Theme';
import { tuning } from '@/config/tuning';
import { forwardX, forwardZ } from '@/math/orientation';
import { asphaltTextures } from '@/world/surfaceTextures';
import { surfaceMaterial } from '@/world/surfaceMaterials';
import { visualCutouts, type PlacedShortcut } from '@/physics/shortcuts';

/**
 * TrackMesh.ts — turns the pure Track data into three.js meshes:
 *  - a road ribbon and a glowing centre line,
 *  - a WIDE, double-sided ground ribbon that follows the track's elevation so
 *    downhills never sink under a flat plane or vanish from below (§6.4),
 *  - glowing guardrail walls on both sides,
 *  - the white goal line under the finish goal (M157).
 *
 * Everything is built once up front (the course is static), so this is cheap.
 */

type Pt = [number, number, number];

/**
 * M158c: street lighting on the road itself. The road ribbon's uv is (0..1
 * across, node index along), so lamp pools are a pure function of the uv: a
 * warm pool under a kerb lamp every `spacing` nodes, alternating sides, plus
 * an even wash. It rides the asphalt's own colour so the texture still reads
 * through the light. No lights, no geometry: one shader term.
 */
function litStreet(
  mat: THREE.MeshStandardMaterial,
  sl: NonNullable<TrackTheme['streetLights']>,
): void {
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.slColor = { value: new THREE.Color(sl.color) };
    shader.uniforms.slStrength = { value: sl.strength };
    shader.uniforms.slAmbient = { value: sl.ambient };
    shader.uniforms.slSpacing = { value: sl.spacing };
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec2 vSlUv;')
      .replace('#include <uv_vertex>', '#include <uv_vertex>\nvSlUv = uv;');
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
        uniform vec3 slColor;
        uniform float slStrength;
        uniform float slAmbient;
        uniform float slSpacing;
        varying vec2 vSlUv;`,
      )
      .replace(
        '#include <emissivemap_fragment>',
        `#include <emissivemap_fragment>
        {
          float cell = vSlUv.y / slSpacing;
          float k = floor(cell + 0.5);
          float d = (cell - k) * slSpacing;
          float side = mod(k, 2.0) < 0.5 ? 0.12 : 0.88;
          float a = vSlUv.x - side;
          float pool = exp(-d * d / 10.0) * exp(-a * a / 0.05);
          vec3 tex = clamp(diffuseColor.rgb * 1.6, 0.0, 1.2);
          totalEmissiveRadiance += slColor * tex * (slStrength * pool + slAmbient);
        }`,
      );
  };
}

/** Build a triangle-strip ribbon between two parallel polylines (a and b). */
function stripGeometry(a: Pt[], b: Pt[]): THREE.BufferGeometry {
  const n = a.length;
  const positions = new Float32Array(n * 2 * 3);
  const uvs = new Float32Array(n * 2 * 2);

  for (let i = 0; i < n; i++) {
    const o = i * 6;
    positions[o] = a[i][0];
    positions[o + 1] = a[i][1];
    positions[o + 2] = a[i][2];
    positions[o + 3] = b[i][0];
    positions[o + 4] = b[i][1];
    positions[o + 5] = b[i][2];
    const u = i * 4;
    uvs[u] = 0;
    uvs[u + 1] = i;
    uvs[u + 2] = 1;
    uvs[u + 3] = i;
  }

  const indices: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    const k = i * 2;
    // Winding chosen so computeVertexNormals() points "up"/"outward".
    indices.push(k, k + 2, k + 1, k + 1, k + 2, k + 3);
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geo.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

// ---- Per-city guardrail designs (M42) — procedural, zero download. ----
// stripGeometry's UVs put u (texture x) BOTTOM→TOP of the wall and v (texture
// y) ALONG the track, one repeat per node (= 4 world units). So each canvas'
// x-axis is height above the road (0 = tarmac, right edge = wall top) and its
// y-axis is one seamless 4 m module along the wall. The city's signature glow
// survives as a bright CAP strip (top ~16%) in the theme wall colour — the
// course still reads at night; the body below it is the city's real barrier.
/** how far the ground sheet reaches past each guardrail */
const GROUND_EXTEND = 80;

const WW = 48; // x: wall height (1.4 m)
const WH = 128; // y: one 4 m module along the track
const CAP = 0.84; // cap strip = top 16% of the wall

let wallSeed = 1;
function wRand(): number {
  wallSeed = (wallSeed * 1664525 + 1013904223) >>> 0;
  return wallSeed / 4294967296;
}
const wrgb = (r: number, g: number, b: number): string => `rgb(${r | 0},${g | 0},${b | 0})`;

function wallCanvas(): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = WW;
  c.height = WH;
  const x = c.getContext('2d');
  if (!x) throw new Error('TrackMesh: no 2D context for wall texture');
  return [c, x];
}

/** Fill a height band [h0..h1] (0 = road, 1 = top) for a length range [a0..a1]
 *  (fractions of the 4 m module). fillStyle is the caller's. */
function band(x: CanvasRenderingContext2D, h0: number, h1: number, a0 = 0, a1 = 1): void {
  x.fillRect(h0 * WW, a0 * WH, (h1 - h0) * WW, (a1 - a0) * WH);
}

/** Paint one city's wall body into `mx` (colour) and its glow into `ex`. */
function paintWallBody(
  style: NonNullable<TrackTheme['wallStyle']>,
  mx: CanvasRenderingContext2D,
  ex: CanvasRenderingContext2D,
  wallCss: string,
): void {
  wallSeed = 0xa11 + style.length * 131;
  const noise = (base: number, spread: number, count: number, tint = 0): void => {
    mx.globalAlpha = 0.18;
    for (let i = 0; i < count; i++) {
      const v = base - spread / 2 + wRand() * spread;
      mx.fillStyle = wrgb(v, v, v - tint);
      mx.fillRect(wRand() * WW * CAP, wRand() * WH, 2, 3);
    }
    mx.globalAlpha = 1;
  };
  switch (style) {
    case 'jersey': {
      // NYC: concrete jersey barrier — darker kick, expansion joint, stains.
      mx.fillStyle = wrgb(162, 162, 158);
      band(mx, 0, CAP);
      mx.fillStyle = wrgb(122, 122, 118);
      band(mx, 0, 0.14);
      noise(160, 40, 90);
      mx.fillStyle = wrgb(108, 108, 104);
      band(mx, 0, CAP, 0, 0.02); // joint every module
      mx.globalAlpha = 0.12;
      mx.fillStyle = wrgb(60, 60, 58);
      for (let i = 0; i < 3; i++) band(mx, 0.14, CAP, wRand(), Math.min(1, wRand() + 0.04));
      mx.globalAlpha = 1;
      break;
    }
    case 'balustrade': {
      // London: Portland-stone parapet — recessed panels between pilasters.
      mx.fillStyle = wrgb(214, 206, 188);
      band(mx, 0, CAP);
      mx.fillStyle = wrgb(192, 184, 166);
      band(mx, 0.1, 0.62, 0.14, 0.94); // recessed panel
      mx.fillStyle = wrgb(150, 143, 128);
      band(mx, 0.6, 0.62, 0.14, 0.94); // panel top shadow
      mx.fillStyle = wrgb(228, 220, 202);
      band(mx, 0, CAP, 0, 0.12); // pilaster at the module edge
      mx.fillStyle = wrgb(232, 224, 206);
      band(mx, 0.68, CAP); // coping course
      noise(205, 26, 60);
      break;
    }
    case 'tufa': {
      // Napoli: warm tufa-block sea wall, two staggered courses.
      mx.fillStyle = wrgb(166, 152, 122);
      band(mx, 0, CAP); // joints
      for (let row = 0; row < 2; row++) {
        const h0 = 0.02 + row * 0.41;
        for (let a = row % 2 === 0 ? 0 : -0.25; a < 1; a += 0.5) {
          const v = 198 + wRand() * 18;
          mx.fillStyle = wrgb(v, v - 12, v - 44);
          band(mx, h0, h0 + 0.37, Math.max(0, a + 0.015), Math.min(1, a + 0.485));
        }
      }
      noise(195, 30, 70, 30);
      break;
    }
    case 'neon': {
      // Tokyo: dark metal panels + a neon strip that also GLOWS (emissive).
      mx.fillStyle = wrgb(26, 30, 38);
      band(mx, 0, CAP);
      mx.globalAlpha = 0.5;
      for (let a = 0; a < 1; a += 0.25) {
        const v = 22 + wRand() * 14;
        mx.fillStyle = wrgb(v, v + 3, v + 8);
        band(mx, 0, CAP, a + 0.01, a + 0.24);
      }
      mx.globalAlpha = 1;
      mx.fillStyle = wallCss;
      band(mx, 0.4, 0.48);
      ex.fillStyle = wallCss;
      band(ex, 0.4, 0.48); // the strip glows at night
      break;
    }
    case 'wave': {
      // Rio: the black & white Copacabana calçadão wave, run along the wall.
      mx.fillStyle = wrgb(236, 233, 224);
      band(mx, 0, CAP);
      mx.strokeStyle = wrgb(24, 24, 26);
      mx.lineWidth = 7;
      const mid = (CAP * WW) / 2;
      for (const phase of [0, Math.PI]) {
        mx.beginPath();
        for (let y = -4; y <= WH + 4; y += 4) {
          const xx = mid + Math.sin((y / WH) * Math.PI * 2 + phase) * mid * 0.55;
          if (y === -4) mx.moveTo(xx, y);
          else mx.lineTo(xx, y);
        }
        mx.stroke();
      }
      break;
    }
    case 'render': {
      // Dakar: sand render with a painted textile band (teal/rust triangles).
      mx.fillStyle = wrgb(214, 196, 160);
      band(mx, 0, CAP);
      noise(200, 30, 70, 30);
      const b0 = 0.3 * WW * CAP + 4;
      const b1 = 0.58 * WW * CAP;
      mx.fillStyle = wrgb(238, 230, 208);
      band(mx, 0.28, 0.6);
      for (let i = 0; i < 8; i++) {
        const y0 = (i / 8) * WH;
        const y1 = ((i + 1) / 8) * WH;
        mx.fillStyle = i % 2 === 0 ? wrgb(42, 138, 138) : wrgb(198, 74, 48);
        mx.beginPath();
        if (i % 2 === 0) {
          mx.moveTo(b0, y0);
          mx.lineTo(b1, (y0 + y1) / 2);
          mx.lineTo(b0, y1);
        } else {
          mx.moveTo(b1, y0);
          mx.lineTo(b0, (y0 + y1) / 2);
          mx.lineTo(b1, y1);
        }
        mx.closePath();
        mx.fill();
      }
      break;
    }
    case 'chevron': {
      // Lagos: concrete with a danfo-yellow hazard kick.
      mx.fillStyle = wrgb(172, 170, 164);
      band(mx, 0, CAP);
      noise(168, 36, 80);
      const kick = 0.32 * WW * CAP;
      mx.save();
      mx.beginPath();
      mx.rect(0, 0, kick, WH);
      mx.clip();
      mx.fillStyle = wrgb(242, 196, 34);
      mx.fillRect(0, 0, kick, WH);
      mx.fillStyle = wrgb(26, 26, 28);
      for (let y = -WH; y < WH * 2; y += 32) {
        mx.beginPath();
        mx.moveTo(0, y);
        mx.lineTo(kick, y + kick);
        mx.lineTo(kick, y + kick + 16);
        mx.lineTo(0, y + 16);
        mx.closePath();
        mx.fill();
      }
      mx.restore();
      break;
    }
    case 'seawall': {
      // Mumbai: Marine-Drive basalt base under a cream parapet.
      mx.fillStyle = wrgb(50, 50, 48);
      band(mx, 0, 0.5); // joints
      for (let a = 0; a < 1; a += 1 / 3) {
        const v = 72 + wRand() * 14;
        mx.fillStyle = wrgb(v, v - 2, v - 6);
        band(mx, 0.02, 0.47, a + 0.015, a + 1 / 3 - 0.015);
      }
      mx.fillStyle = wrgb(224, 214, 192);
      band(mx, 0.5, CAP);
      noise(215, 24, 40);
      break;
    }
    case 'timber': {
      // Patagonia: estancia timber rails on posts (dark shadow between).
      mx.fillStyle = wrgb(46, 40, 34);
      band(mx, 0, CAP);
      for (const [h0, h1] of [
        [0.14, 0.38],
        [0.5, 0.74],
      ]) {
        const v = 146 + wRand() * 16;
        mx.fillStyle = wrgb(v, v * 0.78, v * 0.52);
        band(mx, h0, h1);
        mx.globalAlpha = 0.25;
        mx.strokeStyle = wrgb(90, 66, 42);
        mx.lineWidth = 1;
        for (let g = 0; g < 3; g++) {
          const gx = (h0 + (h1 - h0) * (0.2 + 0.3 * g)) * WW;
          mx.beginPath();
          mx.moveTo(gx, 0);
          mx.bezierCurveTo(gx + 2, WH * 0.3, gx - 2, WH * 0.7, gx + 1, WH);
          mx.stroke();
        }
        mx.globalAlpha = 1;
      }
      mx.fillStyle = wrgb(112, 86, 56);
      band(mx, 0, CAP, 0, 0.09); // post at the module edge
      break;
    }
  }
}

/** Build the wall colour + glow maps for a city style. */
function wallTextures(
  style: NonNullable<TrackTheme['wallStyle']>,
  wallColor: number,
): { map: THREE.CanvasTexture; emissive: THREE.CanvasTexture } {
  const wallCss = `#${wallColor.toString(16).padStart(6, '0')}`;
  const [mc, mx] = wallCanvas();
  const [ec, ex] = wallCanvas();
  ex.fillStyle = '#000';
  ex.fillRect(0, 0, WW, WH);
  paintWallBody(style, mx, ex, wallCss);
  // The glowing cap strip — the course's night-time signature, kept.
  mx.fillStyle = wallCss;
  band(mx, CAP, 1);
  ex.fillStyle = wallCss;
  band(ex, CAP, 1);
  const map = new THREE.CanvasTexture(mc);
  const emissive = new THREE.CanvasTexture(ec);
  for (const t of [map, emissive]) {
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.colorSpace = THREE.SRGBColorSpace;
  }
  return { map, emissive };
}

// ---- Per-city ROAD MARKINGS (M48) — procedural, zero download. ----
// A full-width overlay ribbon above the asphalt. Its canvas: x = ACROSS the
// road (0 = left rail, 1 = right rail), y = ALONG the track, one canvas module
// per RM_NODES nodes (= 48 m). Most styles paint transparent road PAINT (lane
// lines, zebras, wear) over the PBR asphalt; 'cobble' and 'gravel' are opaque
// SURFACE restyles. Colours below are real-world paint conventions per city.
const RW = 512; // x: across the 36 m road (~14 px/m)
const RH = 768; // y: one 48 m module along the track (16 px/m)
export const RM_NODES = 12; // nodes per module (12 × 4 m)

function roadCanvas(): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = RW;
  c.height = RH;
  const x = c.getContext('2d');
  if (!x) throw new Error('TrackMesh: no 2D context for road texture');
  return [c, x];
}

/** Across-the-road lateral (units, - = left) → canvas x px. Road is 36 wide. */
function rx36(lat: number): number {
  return ((lat + 18) / 36) * RW;
}
const PXU = RW / 36; // px per unit across
const PYU = RH / 48; // px per unit along

/** A solid line at `lat`, `w` units wide, over an along range (fractions). */
function rline(x: CanvasRenderingContext2D, lat: number, w: number, a0 = 0, a1 = 1): void {
  x.fillRect(rx36(lat - w / 2), a0 * RH, w * PXU, (a1 - a0) * RH);
}

/** Dashed line: `on`/`off` unit lengths along the 48 m module. */
function rdash(x: CanvasRenderingContext2D, lat: number, w: number, on: number, off: number): void {
  const px = rx36(lat - w / 2);
  const wpx = w * PXU;
  for (let y = 0; y < 48; y += on + off) x.fillRect(px, y * PYU, wpx, on * PYU);
}

/** A soft-edged blob (canvas px): a radial gradient from `rgb` at `a0` out to
 *  zero, squashed to rx × ry and rotated. The shared feathering primitive the
 *  Lagos patch pass proved out — a hard-edged fillRect on the road overlay
 *  always reads as something PAINTED on, never as something that drifted or
 *  wore there. */
function rblob(
  x: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  rot: number,
  rgb: string,
  a0: number,
): void {
  x.save();
  x.translate(cx, cy);
  x.rotate(rot);
  x.scale(1, ry / rx);
  const g = x.createRadialGradient(0, 0, rx * 0.2, 0, 0, rx);
  g.addColorStop(0, `rgba(${rgb},${a0})`);
  g.addColorStop(0.55, `rgba(${rgb},${a0 * 0.7})`);
  g.addColorStop(1, `rgba(${rgb},0)`);
  x.fillStyle = g;
  x.beginPath();
  x.arc(0, 0, rx, 0, Math.PI * 2);
  x.fill();
  x.restore();
}

/** A zebra crossing band: stripes ACROSS the road at along-position a (units). */
function rzebra(x: CanvasRenderingContext2D, a: number, depth: number, alpha: number): void {
  x.globalAlpha = alpha;
  for (let lat = -15; lat < 15; lat += 2.4) {
    x.fillRect(rx36(lat), a * PYU, 1.4 * PXU, depth * PYU);
  }
  x.globalAlpha = 1;
}

/** Paint one city's road markings; returns true if the style is an opaque
 *  SURFACE restyle (lit material) rather than a paint overlay (unlit). */
function paintRoadMarkings(
  style: NonNullable<TrackTheme['roadStyle']>,
  x: CanvasRenderingContext2D,
  lineCss: string,
): boolean {
  wallSeed = 0x40ad + style.length * 977;
  const white = 'rgba(238,240,242,';
  const yellow = 'rgba(240,200,60,';
  switch (style) {
    case 'usDouble': {
      // NYC: double yellow centre, white lane dashes, white edges, street wear.
      x.fillStyle = yellow + '0.9)';
      rline(x, -0.55, 0.32);
      rline(x, 0.55, 0.32);
      x.fillStyle = white + '0.75)';
      rdash(x, -9, 0.3, 3, 5);
      rdash(x, 9, 0.3, 3, 5);
      rline(x, -16.6, 0.35);
      rline(x, 16.6, 0.35);
      // Manhole covers + tar snakes.
      x.fillStyle = 'rgba(20,22,26,0.55)';
      for (let i = 0; i < 3; i++) {
        x.beginPath();
        x.arc(rx36(wRand() * 24 - 12), wRand() * RH, 0.75 * PXU, 0, Math.PI * 2);
        x.fill();
      }
      x.strokeStyle = 'rgba(16,17,20,0.5)';
      x.lineWidth = 3;
      for (let i = 0; i < 4; i++) {
        x.beginPath();
        const sx = rx36(wRand() * 28 - 14);
        const sy = wRand() * RH;
        x.moveTo(sx, sy);
        x.quadraticCurveTo(sx + 40 - wRand() * 80, sy + 60, sx + 30 - wRand() * 60, sy + 140);
        x.stroke();
      }
      return false;
    }
    case 'ukLanes': {
      // London: white centre dashes + the iconic DOUBLE YELLOW no-parking
      // lines hugging both kerbs.
      x.fillStyle = white + '0.8)';
      rdash(x, 0, 0.3, 4, 4);
      x.fillStyle = yellow + '0.85)';
      rline(x, -17.0, 0.22);
      rline(x, -16.4, 0.22);
      rline(x, 17.0, 0.22);
      rline(x, 16.4, 0.22);
      return false;
    }
    case 'cobble': {
      // Napoli: sanpietrini setts — staggered dark basalt blocks with light
      // mortar joints, a faded centre line worn over them.
      x.fillStyle = 'rgb(58,58,64)';
      x.fillRect(0, 0, RW, RH);
      const sw = 0.62 * PXU; // sett size ~0.62 m
      const sh = 0.62 * PYU;
      for (let ry = 0; ry < RH / sh; ry++) {
        const offset = ry % 2 === 0 ? 0 : sw / 2;
        for (let cxp = -sw; cxp < RW + sw; cxp += sw) {
          const v = 52 + wRand() * 26;
          x.fillStyle = wrgb(v, v, v + 5);
          x.fillRect(cxp + offset + 1, ry * sh + 1, sw - 2, sh - 2);
        }
      }
      x.fillStyle = white + '0.28)';
      rline(x, 0, 0.35);
      return true;
    }
    case 'jpTech': {
      // Tokyo: hot-pink centre dashes (the city's night signature), white lane
      // dashes, edge lines, a zebra crossing + painted arrows each module.
      x.fillStyle = lineCss;
      rdash(x, 0, 0.4, 3, 3);
      x.fillStyle = white + '0.7)';
      rdash(x, -9, 0.28, 3, 5);
      rdash(x, 9, 0.28, 3, 5);
      rline(x, -16.6, 0.3);
      rline(x, 16.6, 0.3);
      rzebra(x, 1, 3, 0.55);
      // Forward arrows in the two lanes.
      x.globalAlpha = 0.6;
      for (const lat of [-4.5, 4.5]) {
        const ax = rx36(lat);
        const ay = 30 * PYU;
        x.beginPath();
        x.moveTo(ax, ay - 3.4 * PYU);
        x.lineTo(ax - 0.9 * PXU, ay - 1.2 * PYU);
        x.lineTo(ax - 0.3 * PXU, ay - 1.2 * PYU);
        x.lineTo(ax - 0.3 * PXU, ay);
        x.lineTo(ax + 0.3 * PXU, ay);
        x.lineTo(ax + 0.3 * PXU, ay - 1.2 * PYU);
        x.lineTo(ax + 0.9 * PXU, ay - 1.2 * PYU);
        x.closePath();
        x.fill();
      }
      x.globalAlpha = 1;
      return false;
    }
    case 'brBeach': {
      // Rio: yellow centre dashes, white edges, and a black-&-white calçadão
      // wave band spilling in along each kerb.
      x.fillStyle = yellow + '0.85)';
      rdash(x, 0, 0.32, 3.5, 4.5);
      x.fillStyle = white + '0.7)';
      rline(x, -15.8, 0.3);
      rline(x, 15.8, 0.3);
      for (const side of [-1, 1]) {
        for (let X = rx36(side * 17.4) - 0.9 * PXU; ; ) {
          x.fillStyle = 'rgba(240,238,230,0.55)';
          x.fillRect(X, 0, 1.8 * PXU, RH);
          x.fillStyle = 'rgba(24,24,28,0.55)';
          x.beginPath();
          for (let Y = 0; Y <= RH; Y += 4) {
            const wave = Math.sin((Y / RH) * Math.PI * 6) * 0.55 * PXU;
            if (Y === 0) x.moveTo(X + 0.9 * PXU + wave, Y);
            else x.lineTo(X + 0.9 * PXU + wave, Y);
          }
          x.lineWidth = 0.5 * PXU;
          x.strokeStyle = 'rgba(24,24,28,0.6)';
          x.stroke();
          break;
        }
      }
      return false;
    }
    case 'sahel': {
      // Dakar: one faded, broken centre line; harmattan sand washed over the
      // edges and streaked across the lanes.
      x.fillStyle = white + '0.35)';
      rdash(x, 0, 0.3, 5, 3);
      const sand = x.createLinearGradient(0, 0, RW, 0);
      sand.addColorStop(0, 'rgba(216,180,110,0.55)');
      sand.addColorStop(0.14, 'rgba(216,180,110,0)');
      sand.addColorStop(0.86, 'rgba(216,180,110,0)');
      sand.addColorStop(1, 'rgba(216,180,110,0.55)');
      x.fillStyle = sand;
      x.fillRect(0, 0, RW, RH);
      // dakar-shared-2a: the wash used to be 12 hard-edged fillRects per 48 m
      // module, several of them starting at lat +4 — i.e. mustard BARS lying
      // ACROSS the racing line, on a loop. Sand does not do that: harmattan
      // drift piles against the kerb and thins to nothing as it reaches in.
      // Now each tongue is a feathered blob (the Lagos patch primitive)
      // anchored at a kerb, elongated ALONG the road, yawed a few degrees off
      // the road axis, and TAPERED — a big root blob at the kerb plus a
      // smaller, fainter tip reaching inward, never past lat ±9 (the AI and
      // the racing line both live inside that). Tone is dust, not paint:
      // pale warm grey-sand, lighter than the tarmac it lies on.
      const DUST = '214,192,152';
      for (let i = 0; i < 5; i++) {
        const side = i % 2 === 0 ? -1 : 1;
        const y = ((i + wRand() * 0.8) / 5) * RH;
        const kerb = side * (16.4 - wRand() * 1.6);
        const reach = 5.5 + wRand() * 3.5; // how far inward this tongue creeps
        const yaw = side * (0.05 + wRand() * 0.11); // off-axis, so it drifts
        rblob(
          x,
          rx36(kerb),
          y,
          (5.5 + wRand() * 3) * PYU,
          (2.6 + wRand() * 1.4) * PXU,
          Math.PI / 2 + yaw,
          DUST,
          0.34,
        );
        rblob(
          x,
          rx36(kerb - side * reach),
          y + (wRand() - 0.5) * 60,
          (3.4 + wRand() * 2.4) * PYU,
          (1.3 + wRand() * 0.9) * PXU,
          Math.PI / 2 + yaw * 2.2,
          DUST,
          0.17,
        );
      }
      return false;
    }
    case 'worn': {
      // Lagos: sun-faded broken dashes, patch repairs, oil stains.
      // lagos-ts: the repairs used to be 5 hard-edged near-black rectangles +
      // 4 hard ellipses per 48 m module — from any elevated view they read as
      // grey boxes PASTED down the road on a loop (QA). Now: density down 40%
      // (3 + 2), every patch is ROTATED off-axis, each is filled with a radial
      // gradient so its edges feather into the asphalt instead of cutting it,
      // and the tone lifted from near-black toward fresh-asphalt grey — a
      // resurfaced strip is LIGHTER than the sun-bleached road around it, not
      // darker. Only the oil stains stay genuinely dark.
      x.fillStyle = white + '0.4)';
      rdash(x, 0, 0.3, 2.5, 6);
      rdash(x, -9, 0.26, 2, 7);
      rdash(x, 9, 0.26, 2, 7);
      /** A soft-edged blob: radial gradient from `rgb` at `a0` to zero. */
      const feather = (
        cx: number,
        cy: number,
        rx: number,
        ry: number,
        rot: number,
        rgb: string,
        a0: number,
      ): void => {
        x.save();
        x.translate(cx, cy);
        x.rotate(rot);
        x.scale(1, ry / rx);
        const g = x.createRadialGradient(0, 0, rx * 0.25, 0, 0, rx);
        g.addColorStop(0, `rgba(${rgb},${a0})`);
        g.addColorStop(0.62, `rgba(${rgb},${a0 * 0.82})`);
        g.addColorStop(1, `rgba(${rgb},0)`);
        x.fillStyle = g;
        x.beginPath();
        x.arc(0, 0, rx, 0, Math.PI * 2);
        x.fill();
        x.restore();
      };
      for (let i = 0; i < 3; i++) {
        const w = (2.4 + wRand() * 4) * PXU;
        const h = (3.5 + wRand() * 6) * PYU;
        feather(
          rx36(wRand() * 26 - 13),
          wRand() * RH,
          Math.max(w, h) / 2,
          Math.min(w, h) / 2,
          (wRand() - 0.5) * 0.9 + (h > w ? Math.PI / 2 : 0),
          '76,78,84',
          0.4,
        );
      }
      for (let i = 0; i < 2; i++) {
        feather(
          rx36(wRand() * 20 - 10),
          wRand() * RH,
          2.4 * PYU,
          1.1 * PXU,
          Math.PI / 2 + (wRand() - 0.5) * 0.5,
          '18,18,21',
          0.34,
        );
      }
      return false;
    }
    case 'inMix': {
      // Mumbai: white centre dashes, yellow edge lines, a monsoon-faded zebra.
      x.fillStyle = white + '0.7)';
      rdash(x, 0, 0.3, 3, 4);
      x.fillStyle = yellow + '0.8)';
      rline(x, -16.6, 0.32);
      rline(x, 16.6, 0.32);
      x.fillStyle = white + '1)';
      rzebra(x, 22, 3, 0.4);
      return false;
    }
    case 'gravel': {
      // Patagonia: no paint at all — a gravel surface with a lighter crown and
      // two darker wheel tracks where every vehicle before you has run.
      x.fillStyle = 'rgb(96,92,86)';
      x.fillRect(0, 0, RW, RH);
      for (let i = 0; i < 2600; i++) {
        const v = 78 + wRand() * 44;
        x.fillStyle = wrgb(v, v - 4, v - 10);
        x.fillRect(wRand() * RW, wRand() * RH, 2, 2);
      }
      x.fillStyle = 'rgba(140,134,124,0.35)'; // loose light crown
      rline(x, 0, 3.2);
      x.fillStyle = 'rgba(58,56,52,0.5)'; // compacted wheel tracks
      for (const lat of [-2.4, 2.4, -7.4, 7.4]) rline(x, lat, 1.1);
      return true;
    }
  }
}

/** Build the road-markings overlay texture for a city style. */
function roadMarkingTexture(
  style: NonNullable<TrackTheme['roadStyle']>,
  lineColor: number,
): { tex: THREE.CanvasTexture; surface: boolean } {
  const [c, x] = roadCanvas();
  const surface = paintRoadMarkings(style, x, `#${lineColor.toString(16).padStart(6, '0')}`);
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.repeat.set(1, 1 / RM_NODES); // v = node index → one module per RM_NODES nodes
  tex.anisotropy = 4; // markings stay crisp at grazing view angles
  return { tex, surface };
}

/**
 * The GOAL LINE (M157, owner: "white goal line only — pure soccer"). A crisp
 * white line painted across the road exactly under the goal's posts: its
 * width IS the posts' width, centred on the finish (the sim's rule line — a
 * ball's centre over it is a ball half over the line, so the paint and the
 * rule agree). Replaces the M97 checkered band; the vertical element is the
 * goal (scenery/finishGoal.ts), standing on this line in every city.
 */
/** Where the city's lane paint starts to fade before the goal line, and
 *  where it is fully gone (M157), in units before the line. */
const PAINT_FADE_FROM = 30;
const PAINT_FADE_TO = 6;

function buildGoalLine(track: Track): THREE.Mesh {
  const half = tuning.goal.postRadius;
  const hw = track.halfWidth;
  const a: Pt[] = [];
  const b: Pt[] = [];
  for (const d of [track.finishDistance - half, track.finishDistance + half]) {
    const s = track.sampleAt(d);
    a.push([s.x - s.rightX * hw, s.y + 0.16, s.z - s.rightZ * hw]);
    b.push([s.x + s.rightX * hw, s.y + 0.16, s.z + s.rightZ * hw]);
  }
  // paint, not a light: a soft off-white that still reads at night
  return new THREE.Mesh(stripGeometry(a, b), new THREE.MeshBasicMaterial({ color: 0xeeeee8 }));
}

/** Mumbai-only (mumbai-ts): the start barrier joining the two seawall
 *  guardrails was the bare plainWallMat glow box — a flat untextured ORANGE
 *  slab filling the bottom of the city's very first frame (measured, QA
 *  start-grid). Skin its faces in the rail's own seawall language (basalt
 *  block courses, the cream parapet band, a thin cap strip in the theme
 *  orange so it still ties into the rail glow) and run cream promenade
 *  PAVERS across the wide TOP face the grid camera grazes. Every other city
 *  keeps the plain glow bar — their night palettes want it.
 *  BoxGeometry material order: [+x, −x, +y, −y, +z, −z]. */
function seawallBarrierMats(wallColor: number): THREE.Material[] {
  // Faces: painted top-down (canvas y=0 is texture v=1 = the barrier's top).
  const fc = document.createElement('canvas');
  fc.width = 512;
  fc.height = 64;
  const fx = fc.getContext('2d');
  if (!fx) throw new Error('Could not get 2D context for the start barrier');
  let s = 0x5ea11;
  const rand = (): number => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
  fx.fillStyle = `#${wallColor.toString(16).padStart(6, '0')}`;
  fx.fillRect(0, 0, 512, 8); // thin theme-orange cap line at the top
  fx.fillStyle = 'rgb(224,214,192)';
  fx.fillRect(0, 8, 512, 16); // cream parapet band
  fx.fillStyle = 'rgb(50,50,48)';
  fx.fillRect(0, 24, 512, 40); // basalt joints
  for (let row = 0; row < 2; row++) {
    const off = row * 24;
    for (let bx = -24; bx < 512; bx += 48) {
      const v = 72 + ((rand() * 14) | 0);
      fx.fillStyle = `rgb(${v},${v - 2},${v - 6})`;
      fx.fillRect(bx + off + 1, 25 + row * 20, 46, 18);
    }
  }
  const faceTex = new THREE.CanvasTexture(fc);
  faceTex.colorSpace = THREE.SRGBColorSpace;
  faceTex.wrapS = THREE.RepeatWrapping;
  faceTex.repeat.x = 3; // block modules land ~1.1 u wide across the road
  faceTex.anisotropy = 4;
  // Top: cream pavers, joints across the width only (the 0.5 u depth would
  // stretch any horizontal line), heavy speckle so the slab never reads flat.
  const tc = document.createElement('canvas');
  tc.width = 256;
  tc.height = 32;
  const tx = tc.getContext('2d');
  if (!tx) throw new Error('Could not get 2D context for the start barrier top');
  tx.fillStyle = 'rgb(203,193,170)';
  tx.fillRect(0, 0, 256, 32);
  for (let px = 0; px < 256; px += 32) {
    const v = 196 + ((rand() * 18) | 0);
    tx.fillStyle = `rgb(${v},${v - 8},${v - 26})`;
    tx.fillRect(px + 2, 0, 28, 32);
    tx.fillStyle = 'rgba(40,38,32,0.5)';
    tx.fillRect(px, 0, 2, 32); // paver joint
  }
  for (let i = 0; i < 160; i++) {
    tx.fillStyle = rand() < 0.5 ? 'rgba(0,0,0,0.10)' : 'rgba(255,255,255,0.08)';
    tx.fillRect((rand() * 255) | 0, (rand() * 31) | 0, 2, 2);
  }
  const topTex = new THREE.CanvasTexture(tc);
  topTex.colorSpace = THREE.SRGBColorSpace;
  topTex.wrapS = THREE.RepeatWrapping;
  topTex.repeat.x = 6;
  topTex.anisotropy = 8; // the grid camera sees this face at a grazing angle
  const faceMat = new THREE.MeshStandardMaterial({ map: faceTex, roughness: 0.85 });
  const topMat = new THREE.MeshStandardMaterial({ map: topTex, roughness: 0.9 });
  return [faceMat, faceMat, topMat, topMat, faceMat, faceMat];
}

// ---- GS3: the paved finish FORECOURT APRON (opt-in per city) ---------------
// The plaza's gantry + grandstands used to stand on bare lawn / void. This
// extends the M97 ground-apron idea with a PAVED surface: two verge bands
// flanking the road through the plaza (they sit ABOVE the zone grass sheets),
// and a full-width tail past the end wall so the rollout coasts onto real
// tarmac/pavers instead of diving into the ground sheet. Consumed by
// scenery/finishPlaza.ts via its ROLLOUT_DRESSING table.

export type FinishApronKind = 'tarmac' | 'paver';

/** Procedural paving canvas: one ~6×6 u tile. Direct colour (no tint). */
function apronCanvas(kind: FinishApronKind): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = 128;
  c.height = 128;
  const x = c.getContext('2d');
  if (!x) throw new Error('Could not get 2D context for the finish apron');
  let s = kind === 'paver' ? 0x9a7e1 : 0x51c3d;
  const rand = (): number => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
  if (kind === 'paver') {
    // concrete paver grid: 4×4 blocks with dark joints + per-block tone jitter
    x.fillStyle = '#26282a';
    x.fillRect(0, 0, 128, 128);
    for (let row = 0; row < 4; row++)
      for (let col = 0; col < 4; col++) {
        const v = 118 + ((rand() * 26) | 0);
        x.fillStyle = `rgb(${v},${v - 4},${v - 10})`;
        x.fillRect(col * 32 + 2, row * 32 + 2, 28, 28);
        // subtle worn corner shading
        x.fillStyle = 'rgba(0,0,0,0.12)';
        x.fillRect(col * 32 + 2, row * 32 + 24, 28, 6);
      }
    for (let i = 0; i < 70; i++) {
      x.fillStyle = rand() < 0.5 ? 'rgba(0,0,0,0.10)' : 'rgba(255,255,255,0.05)';
      x.fillRect((rand() * 126) | 0, (rand() * 126) | 0, 2, 2);
    }
  } else {
    // event tarmac: dark asphalt with aggregate speckle + faint tyre scuffs
    x.fillStyle = '#232629';
    x.fillRect(0, 0, 128, 128);
    for (let i = 0; i < 420; i++) {
      const v = rand();
      x.fillStyle = v < 0.5 ? 'rgba(255,255,255,0.045)' : 'rgba(0,0,0,0.14)';
      x.fillRect((rand() * 127) | 0, (rand() * 127) | 0, 1, 1);
    }
    for (let i = 0; i < 4; i++) {
      x.fillStyle = 'rgba(0,0,0,0.10)';
      const sx = (rand() * 110) | 0;
      x.fillRect(sx, 0, 5 + ((rand() * 6) | 0), 128);
    }
  }
  return c;
}

/** Scale a strip's UVs so `tile` world units = one texture repeat. u spans the
 *  strip's width (0..1), v counts sample rows (stripGeometry convention). */
function scaleStripUvs(
  geo: THREE.BufferGeometry,
  width: number,
  rowStep: number,
  tile: number,
): void {
  const uv = geo.getAttribute('uv') as THREE.BufferAttribute;
  for (let i = 0; i < uv.count; i++) {
    uv.setXY(i, (uv.getX(i) * width) / tile, (uv.getY(i) * rowStep) / tile);
  }
  uv.needsUpdate = true;
}

/** Build the paved forecourt apron. Verge bands run alongside the road from
 *  ~70 u before the line to the end wall; the tail continues full-width past
 *  it. Sits 0.12 above the road nodes' base height — over the grass sheets
 *  (≤ +0.07) and clear of the road surface (bands never overlap it). */
export function buildFinishApron(
  track: Track,
  theme: TrackTheme,
  kind: FinishApronKind,
): THREE.Group {
  const group = new THREE.Group();
  const hw = track.halfWidth;
  const fin = track.finishDistance;
  const endArc = track.length + tuning.track.endBarrier + 0.3; // just past the end wall
  const STEP = 6;
  const TILE = 6;
  const LIFT = 0.12;
  const OUTER = hw + 20; // covers stands (to hw+17.5) + a margin
  const last = track.sampleAt(track.length);
  const at = (d: number): { x: number; y: number; z: number; rightX: number; rightZ: number } => {
    if (d <= track.length) return track.sampleAt(d);
    const over = d - track.length;
    return {
      x: last.x + forwardX(last.dir) * over,
      y: last.y,
      z: last.z + forwardZ(last.dir) * over,
      rightX: last.rightX,
      rightZ: last.rightZ,
    };
  };

  const mat = new THREE.MeshStandardMaterial({
    map: (() => {
      const t = new THREE.CanvasTexture(apronCanvas(kind));
      t.colorSpace = THREE.SRGBColorSpace;
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      t.anisotropy = 4;
      return t;
    })(),
    // a whisper of the city's ground tint so the paving sits in its palette
    color: new THREE.Color(theme.ground).lerp(new THREE.Color(0xffffff), 0.85),
    roughness: 0.96,
  });

  const strip = (from: number, to: number, latA: number, latB: number): THREE.Mesh => {
    const aPts: Pt[] = [];
    const bPts: Pt[] = [];
    for (let d = from; d <= to + 0.01; d += STEP) {
      const s = at(Math.min(d, to));
      aPts.push([s.x + s.rightX * latA, s.y + LIFT, s.z + s.rightZ * latA]);
      bPts.push([s.x + s.rightX * latB, s.y + LIFT, s.z + s.rightZ * latB]);
    }
    const geo = stripGeometry(aPts, bPts);
    scaleStripUvs(geo, Math.abs(latB - latA), STEP, TILE);
    const mesh = new THREE.Mesh(geo, mat);
    mesh.receiveShadow = true;
    return mesh;
  };

  // verge bands flanking the road through the plaza
  group.add(strip(fin - 70, endArc, -OUTER, -(hw + 0.35)));
  group.add(strip(fin - 70, endArc, hw + 0.35, OUTER));
  // full-width tail past the end wall (the rollout forecourt)
  group.add(strip(endArc, fin + 132, -OUTER, OUTER));
  return group;
}

/** The ground apron's per-row profile: the extrapolated node list, its arc
 * table, and each side's outer-edge fit (height + the lateral at which the
 * flat verge ends). PURE — no THREE, no DOM — so the GS7 water sink and the
 * GS8 land bank are unit-testable and probe-able headlessly. */
export interface ApronFit {
  /** outer-edge (hw + GROUND_EXTEND) height for this row */
  y: number;
  /** lateral offset past hw at which the flat verge ends and the bank starts */
  flat: number;
}
export interface ApronProfile {
  /** road/rail rows: the start barrier, the course, the end barrier */
  meshNodes: TrackNode[];
  /** meshNodes + the three flat tail rows the ground alone continues onto */
  apronAll: TrackNode[];
  apronArcs: number[];
  sinkL: ApronFit[];
  sinkR: ApronFit[];
  /** true when any row sinks — the sheet then splits into three bands */
  groundSinks: boolean;
}

export function groundApronProfile(track: Track, theme: TrackTheme): ApronProfile {
  // M42: the road/ground/guardrails run OUT past both course ends to the
  // physics barriers, so the grid never sits on the road's edge and finished
  // cars roll out onto real tarmac. Straight, flat extrapolations of the ends.
  const hw = track.halfWidth;
  const groundExtend = GROUND_EXTEND;
  const first = track.nodes[0];
  const last = track.nodes[track.nodes.length - 1];
  const backNode: TrackNode = {
    ...first,
    x: first.x - forwardX(first.dir) * tuning.track.startBarrier,
    z: first.z - forwardZ(first.dir) * tuning.track.startBarrier,
  };
  const endNode: TrackNode = {
    ...last,
    x: last.x + forwardX(last.dir) * tuning.track.endBarrier,
    z: last.z + forwardZ(last.dir) * tuning.track.endBarrier,
  };
  const meshNodes: TrackNode[] = [backNode, ...track.nodes, endNode];
  // M97: the GROUND (only) continues as a flat apron well past the end wall,
  // so the landmarks staged behind the finish (the NYE tower, the Maracanã
  // bowl, Fitz Roy) stand on real ground instead of floating over the void.
  const apronNodes: TrackNode[] = [55, 115, 180].map((d) => ({
    ...last,
    x: last.x + forwardX(last.dir) * d,
    z: last.z + forwardZ(last.dir) * d,
  }));

  // GS7 (mumbai-nits): THE FOREIGN-PASS APRON SINK. The ground ribbon reaches
  // hw+80 past each rail AT ITS OWN NODE'S HEIGHT — where the looping course
  // curls back on itself at a different elevation (Mumbai's Sea Link S-bend),
  // a HIGHER stretch's apron corners sweep flat across a LOWER stretch's
  // world and poke through its water as floating olive slabs (measured: lifts
  // up to +2.7 over the bay surface; the exact class GS1's water course-clamp
  // was built to duck under). Fix: each outer-edge vertex whose world
  // position projects onto a DIFFERENT pass (arc gap > 90 — the water
  // system's foreignArcGap) that sits LOWER (> 1.2 under the node) sinks to
  // that local terrain height, so the apron dives like a natural bank and
  // buries under the local water (surface = local ground + 0.13) instead of
  // shelving over it. Smoothed ±2 nodes so the equidistant ridge between two
  // passes can never sawtooth the edge; min() = only ever sink, never raise.
  // ARC GAP 50, not the water system's 90 (measured on the Mumbai bay): the
  // S-bend's limbs sit only 54-88 arc apart at apron reach, so at 90 the
  // exact rows that poke never fired. 50 also lets a climbing hairpin's
  // inside band ease down toward its lower limb — the same artifact class,
  // on land — while the 1.2 elevation gate keeps flat/level ground pinned.
  const apronAll = [...meshNodes, ...apronNodes];
  const apronArcs: number[] = [];
  {
    let arc = -tuning.track.startBarrier;
    for (let i = 0; i < apronAll.length; i++) {
      if (i > 0) {
        const p = apronAll[i - 1];
        const nd = apronAll[i];
        arc += Math.hypot(nd.x - p.x, nd.z - p.z);
      }
      apronArcs.push(arc);
    }
  }
  // GS8 (shared-2a): THE SAME RULE ON DRY LAND — the self-overlap BANK.
  // The water gate above left every LAND overlap on the flat sheet, and that
  // is the Napoli block crease: two passes of the loop each lay a ribbon over
  // the same courtyard at their OWN elevation, so one shelves over the other
  // (measured 0-4.3 u on the QA seed, ~1.0 u on the tour seed) and where the
  // two cross at equal height the coplanar sheets z-fight into a fan of dark
  // slivers. Track.heightAt can't see it — it is single-valued, picks the
  // nearest segment, and reads perfectly smooth right through the crease.
  // TWO changes, both inside this one function:
  //  (a) the gate now also fires when the two passes are COPLANAR (|dy| ≤ 1.2
  //      — the z-fight band). Both sheets would then want to dive, so the
  //      tie-break is arc order: the LATER pass yields and the earlier one
  //      keeps the visible surface. Deterministic, so exactly one dives.
  //  (b) a land bank keeps its VERGE WIDE (hw + BANK_FLAT) where a water sink
  //      keeps it at hw + 4. That is the whole reason an earlier unconditional
  //      land sink was rejected: sinking from the rail tilted the entire 76 u
  //      band and read as a false wadi carved through Dakar's dunes. Banking
  //      only the outer 38 u leaves the ground under the props flat (measured:
  //      NO city places a single instance past lateral 80, and ≤ 11 per city
  //      past lateral 60) and reads as a natural bank down to the lower pass.
  // Windows are deliberately NOT used for land: unlike route-defined water,
  // where a loop folds back on itself is SEED-dependent (Napoli's crease sits
  // at dest 0.145 on the tour seed and 0.171 on QA seed 123), so a declared
  // arc list could only ever be right for one seed.
  const BANK_FLAT = 42; // land banks start at hw+42 (lateral 60); water at hw+4
  const VERGE_FLAT = 4;
  // A land bank never cuts more than this below its OWN row, so the ground
  // under a prop can only ever ease, never fall off a cliff. Measured: every
  // land crease this pass targets is 0-4.3 u deep, so 4 clears them, and a
  // prop at lateral 70 can shift at most 4 × (70−60)/38 ≈ 1.05 u.
  const BANK_MAX = 4;
  const sinkWindows = theme.apronSink ?? [];
  type EdgeFit = { y: number; flat: number };
  const edgeSink = (side: -1 | 1): EdgeFit[] => {
    // Target 1.2 UNDER the foreign terrain (its water rides at ground + 0.13,
    // its own apron at ground − 0.5 — both stay safely above the sunk band).
    const raw = apronAll.map<EdgeFit>((nd, i) => {
      const keep = { y: nd.y, flat: BANK_FLAT }; // no sink → the verge width is
      // geometrically irrelevant (both columns sit at nd.y), so BANK_FLAT is
      // the neutral value that lets the min() below spread water's narrow 4.
      const ex = nd.x + nd.rightX * side * (hw + groundExtend);
      const ez = nd.z + nd.rightZ * side * (hw + groundExtend);
      const pr = track.project(ex, ez); // hint-less full scan (the M99 rule)
      if (Math.abs(pr.distance - apronArcs[i]) <= 50) return keep;
      const dy = nd.y - pr.y; // > 0 = our sheet shelves ABOVE the foreign pass
      // A DESTINATION declared open water (theme.apronSink) dives from the
      // rail — the edge point sits on window.side of the pass it projects
      // onto, inside the window's arc range. That path is GS7, untouched:
      // same gate, same depth, same narrow verge, so every water city renders
      // exactly as it was verified. Everything else takes the GS8 land bank.
      const frac = pr.distance / track.finishDistance;
      const destSide = pr.lateral >= 0 ? 1 : -1;
      const inWater = sinkWindows.some(
        (w) => destSide === w.side && frac >= w.from && frac <= w.to,
      );
      if (inWater) return dy > 1.2 ? { y: pr.y - 1.2, flat: VERGE_FLAT } : keep;
      const coplanar = dy > -1.2 && apronArcs[i] > pr.distance; // (a) tie-break
      if (dy <= 1.2 && !coplanar) return keep;
      const y = Math.max(pr.y - 1.2, nd.y - BANK_MAX);
      // (b) the verge width is graded by DEPTH, not by a land/water label. A
      // coplanar yield only ever drops 1.2 u, so it can start at the rail like
      // water does — a 1.6% tilt nobody reads as terrain, but enough to pull
      // the whole sheet clear of the one it was z-fighting. Only a DEEP cut
      // needs holding off to hw+BANK_FLAT; that is the wadi guard, and it now
      // applies exactly where the wadi risk actually lives.
      const depth = nd.y - y;
      const t = Math.min(1, Math.max(0, (depth - 1.2) / 2));
      return { y, flat: VERGE_FLAT + (BANK_FLAT - VERGE_FLAT) * t };
    });
    // Erode (windowed MIN ±2) then smooth (avg ±1): a plain box-average let
    // the unsunk shoulder rows LIFT the sink right at the bend's entry — the
    // measured 0.5u sliver that survived the first pass. The min pass can
    // only ever deepen, so shoulders ramp DOWN into the sink, never float.
    const minWin = (i: number, get: (f: EdgeFit) => number, r = 2): number => {
      let v = get(raw[i]);
      for (let k = -r; k <= r; k++) {
        const j = i + k;
        if (j >= 0 && j < raw.length) v = Math.min(v, get(raw[j]));
      }
      return v;
    };
    const eroded = raw.map<EdgeFit>((_, i) => ({
      y: minWin(i, (f) => f.y),
      // The verge WIDTH takes the same min (a water window's narrow verge, or
      // a shallow coplanar yield, always wins over a neighbouring deep bank)
      // but over a much wider run: measured, a width that swings 4 → 42 in
      // three rows folds the verge/bank seam through 38 u sideways in 12 u of
      // course, and computeVertexNormals on that near-degenerate quad shades
      // it as a black spur standing in the block interior. Width is not
      // height — over-smoothing it costs nothing.
      flat: minWin(i, (f) => f.flat, 6),
    }));
    const box = (i: number, get: (f: EdgeFit) => number, r: number): number => {
      let s = 0;
      let n = 0;
      for (let k = -r; k <= r; k++) {
        const j = i + k;
        if (j < 0 || j >= eroded.length) continue;
        s += get(eroded[j]);
        n++;
      }
      return s / n;
    };
    return eroded.map<EdgeFit>((_, i) => ({
      y: Math.min(apronAll[i].y, box(i, (f) => f.y, 1)),
      flat: box(i, (f) => f.flat, 6),
    }));
  };
  const sinkL = edgeSink(-1);
  const sinkR = edgeSink(1);
  const groundSinks = apronAll.some(
    (nd, i) => sinkL[i].y < nd.y - 0.01 || sinkR[i].y < nd.y - 0.01,
  );

  return { meshNodes, apronAll, apronArcs, sinkL, sinkR, groundSinks };
}

export function buildTrackMesh(
  track: Track,
  theme: TrackTheme,
  shortcuts: readonly PlacedShortcut[] = [],
): THREE.Group {
  const group = new THREE.Group();
  const hw = track.halfWidth;
  // M117 world cuts: real openings in the guardrail (doorways, the arch-
  // viaduct + cliff-face spans) and ground-sheet holes for tunnel trenches.
  const cut = visualCutouts(track, shortcuts);

  // Vertical offsets keep coplanar ribbons from z-fighting. The gaps must be
  // generous: at ~400m with a 0.1 near plane, the depth buffer only resolves
  // ~0.1 units — slimmer gaps band/shimmer at distance (worst on pale ground).
  const yRoad = 0.04;
  const yLine = 0.18;
  const yGround = -0.5;
  const wallH = 1.4;
  const lineHalf = 0.3;

  const roadL: Pt[] = [];
  const roadR: Pt[] = [];
  const lineL: Pt[] = [];
  const lineR: Pt[] = [];
  const groundL: Pt[] = [];
  const groundR: Pt[] = [];
  const wallLb: Pt[] = [];
  const wallLt: Pt[] = [];
  const wallRb: Pt[] = [];
  const wallRt: Pt[] = [];

  // M42: the road/ground/guardrails run OUT past both course ends to the
  // physics barriers, so the grid never sits on the road's edge and finished
  // cars roll out onto real tarmac; the GROUND alone continues further still
  // (M97) so the landmarks staged behind the finish stand on real ground.
  // Both node lists — and the apron's per-row sink/verge fit — come from the
  // pure profile below.
  const { meshNodes, apronAll, sinkL, sinkR, groundSinks } = groundApronProfile(track, theme);
  const groundExtend = GROUND_EXTEND;

  for (const nd of meshNodes) {
    const rx = nd.rightX;
    const rz = nd.rightZ;
    roadL.push([nd.x - rx * hw, nd.y + yRoad, nd.z - rz * hw]);
    roadR.push([nd.x + rx * hw, nd.y + yRoad, nd.z + rz * hw]);
    wallLb.push([nd.x - rx * hw, nd.y, nd.z - rz * hw]);
    wallLt.push([nd.x - rx * hw, nd.y + wallH, nd.z - rz * hw]);
    wallRb.push([nd.x + rx * hw, nd.y, nd.z + rz * hw]);
    wallRt.push([nd.x + rx * hw, nd.y + wallH, nd.z + rz * hw]);
  }
  for (const [i, nd] of apronAll.entries()) {
    const rx = nd.rightX;
    const rz = nd.rightZ;
    groundL.push([
      nd.x - rx * (hw + groundExtend),
      sinkL[i].y + yGround,
      nd.z - rz * (hw + groundExtend),
    ]);
    groundR.push([
      nd.x + rx * (hw + groundExtend),
      sinkR[i].y + yGround,
      nd.z + rz * (hw + groundExtend),
    ]);
  }
  // The racing line stays on the course proper (start line → the end).
  for (const nd of track.nodes) {
    const rx = nd.rightX;
    const rz = nd.rightZ;
    lineL.push([nd.x - rx * lineHalf, nd.y + yLine, nd.z - rz * lineHalf]);
    lineR.push([nd.x + rx * lineHalf, nd.y + yLine, nd.z + rz * lineHalf]);
  }

  // Ground (double-sided so descents never reveal a hole — §6.4). A city can opt
  // into a real PBR verge (concrete/sand/grass); otherwise it stays a flat tint.
  // (M117c: the tunnel-trench ground holes are gone with the tunnels — every
  // shortcut is aerial now, so the sheet is whole again.)
  const groundMat = theme.groundTex
    ? surfaceMaterial(theme.groundTex, THREE.DoubleSide)
    : new THREE.MeshStandardMaterial({ color: theme.ground, roughness: 1, side: THREE.DoubleSide });
  if (groundSinks) {
    // GS7: a sinking city splits the sheet into three strips sharing the same
    // vertices at the seams — a flat VERGE band (where the rails, shorelines
    // and kerb props live, exactly as before) and two OUTER bands that tilt
    // from the verge down to the sunk edge like a natural bank. The 2-column
    // sheet couldn't express this: sinking its edge would have dragged the
    // ground at the rail down ~59% of the sink with it. GS8: the verge's
    // WIDTH is now per row and per side (hw+4 under a water window, hw+42
    // for a land bank) — see the edgeSink note. Where nothing sinks both
    // columns sit at nd.y, so the split surface is identical to the old
    // single sheet no matter where the seam falls.
    const vergeL: Pt[] = [];
    const vergeR: Pt[] = [];
    for (const [i, nd] of apronAll.entries()) {
      const fl = hw + sinkL[i].flat;
      const fr = hw + sinkR[i].flat;
      vergeL.push([nd.x - nd.rightX * fl, nd.y + yGround, nd.z - nd.rightZ * fl]);
      vergeR.push([nd.x + nd.rightX * fr, nd.y + yGround, nd.z + nd.rightZ * fr]);
    }
    for (const geo of [
      stripGeometry(groundL, vergeL),
      stripGeometry(vergeL, vergeR),
      stripGeometry(vergeR, groundR),
    ]) {
      const band = new THREE.Mesh(geo, groundMat);
      band.receiveShadow = true;
      group.add(band);
    }
  } else {
    // No overlap anywhere → byte-for-byte the old single sheet.
    const ground = new THREE.Mesh(stripGeometry(groundL, groundR), groundMat);
    ground.receiveShadow = true;
    group.add(ground);
  }

  // Road — a city can opt into a real cracked-tarmac PBR surface (premium realism);
  // otherwise it's the per-city tint over the shared procedural asphalt grain +
  // normal map (M35), so the tarmac catches the sun/IBL instead of flat colour.
  let roadMat: THREE.MeshStandardMaterial;
  if (theme.roadTex) {
    roadMat = surfaceMaterial(theme.roadTex);
  } else {
    const asphalt = asphaltTextures();
    roadMat = new THREE.MeshStandardMaterial({
      color: theme.road,
      roughness: 0.94,
      map: asphalt.detail,
      normalMap: asphalt.normal,
      normalScale: new THREE.Vector2(0.55, 0.55),
    });
  }
  if (theme.streetLights) litStreet(roadMat, theme.streetLights);
  const road = new THREE.Mesh(stripGeometry(roadL, roadR), roadMat);
  road.receiveShadow = true;
  group.add(road);

  // Road identity (M48): a city with a `roadStyle` gets its city-true painted
  // markings (or a full cobble/gravel surface restyle) as a transparent
  // overlay ribbon; the legacy glowing centre line only ships without one.
  if (theme.roadStyle) {
    const { tex, surface } = roadMarkingTexture(theme.roadStyle, theme.roadLine);
    let markL: Pt[] = roadL.map(([px, py, pz]) => [px, py + 0.07, pz]);
    let markR: Pt[] = roadR.map(([px, py, pz]) => [px, py + 0.07, pz]);
    // M157 (owner: "white goal line only"): the city's lane PAINT fades out
    // on the run-in to the goal and is gone from the goal mouth, so nothing
    // (a Tokyo or Mumbai zebra, NYC's double yellow) competes with the white
    // goal line. Surface restyles (cobbles/gravel) are the road itself and
    // run on. meshNodes = [back barrier, ...track.nodes, end barrier].
    const arcOf = (i: number): number =>
      i === 0 ? -tuning.track.startBarrier : Math.min(track.length, (i - 1) * track.step);
    const fadeFrom = track.finishDistance - PAINT_FADE_FROM;
    const fadeTo = track.finishDistance - PAINT_FADE_TO;
    let fade: Float32Array | null = null;
    if (!surface) {
      let keep = markL.length;
      for (let i = 0; i < markL.length; i++) {
        if (arcOf(i) > fadeTo) {
          keep = i + 1;
          break;
        }
      }
      markL = markL.slice(0, keep);
      markR = markR.slice(0, keep);
      fade = new Float32Array(keep * 2 * 4).fill(1);
      for (let i = 0; i < keep; i++) {
        const t = Math.min(1, Math.max(0, (arcOf(i) - fadeFrom) / (fadeTo - fadeFrom)));
        const alpha = 1 - t * t * (3 - 2 * t);
        fade[i * 8 + 3] = alpha;
        fade[i * 8 + 7] = alpha;
      }
    }
    // Paint overlays are UNLIT (white paint stays luminous at night, matching
    // the game's glow language); surface restyles (cobbles/gravel) are LIT so
    // the sun/IBL still shapes them.
    const markMat = surface
      ? new THREE.MeshStandardMaterial({ map: tex, roughness: 0.95, transparent: true })
      : new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false, vertexColors: true });
    const markGeo = stripGeometry(markL, markR);
    if (fade) markGeo.setAttribute('color', new THREE.BufferAttribute(fade, 4));
    const markings = new THREE.Mesh(markGeo, markMat);
    if (surface) markings.receiveShadow = true;
    group.add(markings);
  } else {
    // Centre line (lightly glowing) — the pre-M48 default.
    const line = new THREE.Mesh(
      stripGeometry(lineL, lineR),
      new THREE.MeshStandardMaterial({
        color: theme.roadLine,
        emissive: theme.roadLine,
        emissiveIntensity: 0.35,
        roughness: 0.6,
      }),
    );
    group.add(line);
  }

  // Guardrail walls. M42: a city with a `wallStyle` gets its own barrier design
  // (jersey / balustrade / wave mosaic / timber...) under a glowing cap strip in
  // the theme colour; without one it stays the classic full-glow rail.
  const plainWallMat = new THREE.MeshStandardMaterial({
    color: theme.wall,
    emissive: theme.wall,
    emissiveIntensity: 0.7,
    roughness: 0.4,
    side: THREE.DoubleSide,
  });
  let wallMat = plainWallMat;
  if (theme.wallStyle) {
    const wt = wallTextures(theme.wallStyle, theme.wall);
    wallMat = new THREE.MeshStandardMaterial({
      map: wt.map,
      emissive: 0xffffff,
      emissiveMap: wt.emissive,
      emissiveIntensity: 1.0,
      roughness: 0.7,
      side: THREE.DoubleSide,
    });
  }
  if (cut.wallGaps.length === 0) {
    group.add(new THREE.Mesh(stripGeometry(wallLb, wallLt), wallMat));
    group.add(new THREE.Mesh(stripGeometry(wallRb, wallRt), wallMat));
  } else {
    // M117: the guardrail leaves REAL openings at the shortcut doorways (and
    // whole spans where the shortcut's own structure — the arch viaduct, the
    // cliff face — replaces the wall). Physics is untouched: the lateral clamp
    // stands everywhere except the placed doorway logic, so a solid decoy arch
    // bumps exactly like the wall it visually replaces.
    const gapAt = (segIdx: number, side: -1 | 1): boolean => {
      const midArc = (segIdx - 0.5) * track.step;
      return cut.wallGaps.some((g) => g.side === side && midArc >= g.fromArc && midArc <= g.toArc);
    };
    const addWallRuns = (bottom: Pt[], top: Pt[], side: -1 | 1): void => {
      let s0 = 0; // first segment of the current run (segments are 0..len−2)
      for (let i = 0; i < bottom.length - 1; i++) {
        if (!gapAt(i, side)) continue;
        if (i > s0) {
          group.add(
            new THREE.Mesh(stripGeometry(bottom.slice(s0, i + 1), top.slice(s0, i + 1)), wallMat),
          );
        }
        s0 = i + 1;
      }
      if (bottom.length - 1 > s0) {
        group.add(new THREE.Mesh(stripGeometry(bottom.slice(s0), top.slice(s0)), wallMat));
      }
    };
    addWallRuns(wallLb, wallLt, -1);
    addWallRuns(wallRb, wallRt, 1);
  }

  // M42: cross-road barrier walls closing both course ends (they sit exactly on
  // the physics planes, joining the two guardrails). The START one stays LOW:
  // the idle chase camera sits just behind it on the grid, so at full rail
  // height it curtains the bottom of the countdown frame. The END one now
  // stands far behind the goal (M157 — the net stops everything well short
  // of it), so it wears the same plain barrier skin as the start: the old
  // checkered finish skin went with the checkered line (owner: no checker).
  // Mumbai-ts: the seawall city dresses its start barrier (see the painter).
  const barrierMat: THREE.Material | THREE.Material[] =
    theme.wallStyle === 'seawall' ? seawallBarrierMats(theme.wall) : plainWallMat;
  for (const [nd, h, mat] of [
    [meshNodes[0], 0.85, barrierMat],
    [meshNodes[meshNodes.length - 1], wallH, barrierMat],
  ] as const) {
    const bar = new THREE.Mesh(new THREE.BoxGeometry(hw * 2 + 0.6, h, 0.5), mat);
    bar.position.set(nd.x, nd.y + h / 2, nd.z);
    bar.rotation.y = nd.dir; // local +X spans the road (same convention as the band)
    group.add(bar);
  }

  group.add(buildGoalLine(track));
  return group;
}
